import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { CommunicationPreferences } from '../../types';

export default function Section2Communication() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<CommunicationPreferences>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.communication_preferences) {
      setData(state.formData.communication_preferences);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.communication_preferences, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Save data when it changes
    if (state.intake && data.intake_id) {
      saveSection('communication_preferences', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof CommunicationPreferences, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Communication Preferences</h2>
        
        <div className="space-y-6">
          {/* First Time Selling */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Is this your first time selling a property?
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="first_time_selling"
                  value="true"
                  checked={data.first_time_selling === true}
                  onChange={() => updateField('first_time_selling', true)}
                  className="mr-2"
                />
                Yes
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="first_time_selling"
                  value="false"
                  checked={data.first_time_selling === false}
                  onChange={() => updateField('first_time_selling', false)}
                  className="mr-2"
                />
                No
              </label>
            </div>
          </div>

          {/* Preferred Communication Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              How often would you like to touch base with us? (Mark one)
            </label>
            <div className="space-y-3">
              {[
                { value: 'always_informed', label: 'As much as possible- I\'d like to be completely informed of everything as it happens!' },
                { value: 'occasional', label: 'Just check in occasionally. We\'d like a few updates here or there.' },
                { value: 'major_only', label: 'Please don\'t reach out to us unless it\'s a major event! Our inbox & voicemail is already overflowing.' },
              ].map((option) => (
                <label key={option.value} className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="preferred_communication_frequency"
                    value={option.value}
                    checked={data.preferred_communication_frequency === option.value}
                    onChange={() => updateField('preferred_communication_frequency', option.value)}
                    className="mt-0.5 mr-3 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Preferred Communication Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Method Of Communication?
            </label>
            <div className="flex flex-wrap gap-4">
              {[
                { value: 'text', label: 'Text' },
                { value: 'call', label: 'Call' },
                { value: 'email', label: 'Email' },
              ].map((option) => (
                <label key={option.value} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="preferred_communication_method"
                    value={option.value}
                    checked={data.preferred_communication_method === option.value}
                    onChange={() => updateField('preferred_communication_method', option.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* How Heard About Us */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How did you hear about us?
            </label>
            <select
              value={data.how_heard_about_us || ''}
              onChange={(e) => updateField('how_heard_about_us', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Select option</option>
              <option value="referral">Recommended by friend, family, neighbor, etc.</option>
              <option value="google">Google Search</option>
              <option value="social_media">Social Media</option>
              <option value="zillow">Zillow</option>
              <option value="postcard">Postcard</option>
              <option value="agent_referral">Referral by another agent</option>
              <option value="other">Other</option>
            </select>
            
            {data.how_heard_about_us === 'other' && (
              <input
                type="text"
                value={(data as any).how_heard_about_us_other || ''}
                onChange={(e) => updateField('how_heard_about_us_other' as any, e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Please specify how you heard about us"
              />
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            This information helps us communicate with you in the way that works best for your schedule 
            and preferences. All fields are optional except where marked with an asterisk (*).
          </p>
        </div>
      </div>
    </div>
  );
}
